import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Swords, Check, Volume2, ShieldAlert, Timer } from "lucide-react";
import { createPortal } from "react-dom";

export default function MatchFoundModal({ match, user, onAccept, onCancel }) {
  const [hasAccepted, setHasAccepted] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(25);

  useEffect(() => {
    if (!match) return;
    const interval = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [match]);

  if (!match) return null;

  const totalReq = match.player_ids?.length || 10;
  const acceptedCount = Object.keys(match.accepted_players || {}).length;
  const isSelfAccepted = hasAccepted || (user && match.accepted_players?.[user.id]);

  const handleAcceptClick = () => {
    setHasAccepted(true);
    if (onAccept) onAccept(match.id);
  };

  return createPortal(
    <div className="fixed inset-0 z-[999999] flex items-center justify-center p-4 bg-black/85 backdrop-blur-xl">
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        className="w-full max-w-lg p-8 rounded-3xl bg-[#080a0f] border-2 border-orange-500/80 shadow-[0_0_80px_rgba(255,79,0,0.35)] text-center relative overflow-hidden"
      >
        {/* Animated Background Pulse */}
        <div className="absolute inset-0 bg-gradient-to-t from-orange-500/10 via-transparent to-transparent pointer-events-none animate-pulse" />

        {/* Top Header Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-orange-500/20 border border-orange-500/40 text-orange-400 text-xs font-black uppercase tracking-widest mb-4">
          <Swords className="w-4 h-4 animate-bounce" /> MECI GĂSIT • {match.mode?.toUpperCase()} ARENA
        </div>

        <h2 className="text-2xl sm:text-3xl font-black text-white uppercase tracking-wider mb-2">
          EȘTI PREGĂTIT DE LUPTĂ?
        </h2>
        <p className="text-xs text-zinc-400 max-w-sm mx-auto mb-6">
          Harta selectată: <span className="text-white font-bold uppercase">{match.map || "de_dust2"}</span>. Confirmă prezența înainte de expirarea timpului.
        </p>

        {/* Circular / Linear Timer Progress */}
        <div className="relative w-full h-3 bg-black/60 rounded-full overflow-hidden border border-white/10 mb-6">
          <div 
            className="h-full bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 transition-all duration-1000 ease-linear rounded-full shadow-[0_0_15px_#f97316]"
            style={{ width: `${(secondsLeft / 25) * 100}%` }}
          />
        </div>

        {/* Players Ready Status Badges */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <span className="text-xs font-mono font-bold text-zinc-400">
            Jucători confirmați: <strong className="text-orange-400 text-sm">{acceptedCount}/{totalReq}</strong>
          </span>
        </div>

        {/* Big Glow ACCEPT Button */}
        <div className="flex flex-col gap-3">
          <button
            onClick={handleAcceptClick}
            disabled={isSelfAccepted}
            className={`w-full py-4 rounded-2xl font-black uppercase tracking-widest text-base sm:text-lg transition-all duration-300 shadow-2xl flex items-center justify-center gap-3 ${
              isSelfAccepted
                ? "bg-emerald-500 text-black shadow-emerald-500/40 cursor-default"
                : "bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 hover:from-orange-400 hover:to-amber-400 text-black shadow-orange-500/50 hover:scale-105 active:scale-95 animate-pulse"
            }`}
          >
            {isSelfAccepted ? (
              <>
                <Check className="w-6 h-6 stroke-[3]" /> CONFIRMAT! SE AȘTEAPTĂ CEILALȚI ({secondsLeft}s)
              </>
            ) : (
              <>
                <Swords className="w-6 h-6" /> ACCEPTĂ MECIUL ({secondsLeft}s)
              </>
            )}
          </button>
        </div>
      </motion.div>
    </div>,
    document.body
  );
}
